import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { SideModal } from './SideModal'
import { inputCls } from './finance-shared'
import { PanelShell, PlnBarra, PlnBarraRot, PlnBarraSep, PlnBloco, stickyTh, stickyFoot } from './PanelShell'
import type { FinView } from './FinSidebar'
import {
  FiLoader, FiArrowLeft, FiDownload, FiPrinter, FiFileText, FiTrendingUp, FiPieChart,
  FiList, FiStar, FiClock, FiChevronRight, FiChevronLeft, FiChevronDown, FiTrash2, FiSave,
  FiAlertTriangle,
} from 'react-icons/fi'

// Hub de relatórios (PRD §5.3). Landing com abas Favoritos|Padrão|Personalizados|
// Antigos; a Padrão lista os relatórios reais. O DRE é o relatório-âncora e abre
// inline aqui; Fluxo de caixa e Análise por categoria navegam pras telas prontas.

const brl = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const thisMonth = () => new Date().toISOString().slice(0, 7)
const monthLabel = (m: string) => {
  const [y, mo] = m.split('-').map(Number)
  return new Date(Date.UTC(y, mo - 1, 1)).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric', timeZone: 'UTC' })
}
const monthLabelLong = (m: string) => {
  const [y, mo] = m.split('-').map(Number)
  return new Date(Date.UTC(y, mo - 1, 1)).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}
const addMonth = (m: string, n: number) => {
  const [y, mo] = m.split('-').map(Number)
  const d = new Date(Date.UTC(y, mo - 1 + n, 1))
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}
const ptDate = (s: string) => s.split('-').reverse().join('/')

type Base = 'dre' | 'relacao'
type Cfg = Record<string, string>

export function RelatoriosHub({ onNavigate }: { onNavigate: (v: FinView) => void }) {
  const [report, setReport] = useState<{ base: Base; config?: Cfg } | null>(null)
  if (report?.base === 'dre') return <DREReport initial={report.config} onBack={() => setReport(null)} />
  if (report?.base === 'relacao') return <RelacaoReport initial={report.config} onBack={() => setReport(null)} />
  return <Hub onNavigate={onNavigate} onOpen={(base, config) => setReport({ base, config })} />
}

// Modal simples pra nomear e salvar a configuração atual como relatório personalizado.
function SaveReportModal({ base, config, onClose, onSaved }: { base: Base; config: Cfg; onClose: () => void; onSaved: () => void }) {
  const { authenticatedFetch } = useAuth()
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const save = async () => {
    if (!name.trim()) { setError('Dê um nome ao relatório'); return }
    setBusy(true); setError(null)
    try {
      const r = await authenticatedFetch('/api/saved-reports', { method: 'POST', body: JSON.stringify({ name: name.trim(), base, config }) })
      const d = await r.json()
      if (d.success) onSaved(); else setError(d.error || 'Falha ao salvar')
    } catch { setError('Erro de conexão') } finally { setBusy(false) }
  }
  return (
    <SideModal title="Salvar como personalizado" subtitle={base === 'dre' ? 'DRE' : 'Relação a pagar/receber'} icon={FiStar} onClose={onClose}
      footer={<>
        <button onClick={onClose} className="neu-convex h-9 rounded-lg border border-border px-3.5 text-sm hover:bg-secondary">Cancelar</button>
        <button onClick={save} disabled={busy} className="neu-relief h-9 inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-3.5 text-sm font-medium hover:opacity-90 disabled:opacity-60">{busy && <FiLoader className="w-4 h-4 animate-spin" />} Salvar</button>
      </>}>
      {/* PLANOS (21/10/2026, subtelas): corpo da gaveta numa caixa com respiro, rótulo ≥ 12.5px */}
      {error && <div className="neu-relief rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2 text-sm text-red-600 dark:text-red-400">{error}</div>}
      <div className="rounded-xl border border-border bg-card p-4 space-y-3">
        <p className="text-[13px] text-muted-foreground">Salva os filtros atuais (período, regime, tipo) pra reabrir depois na aba Personalizados.</p>
        <label className="block">
          <span className="text-[12.5px] font-medium text-muted-foreground">Nome do relatório</span>
          <input value={name} onChange={e => setName(e.target.value)} autoFocus className={inputCls + ' mt-1.5'} placeholder="Ex.: DRE trimestre — caixa" />
        </label>
      </div>
    </SideModal>
  )
}

// ── Landing ──
type TabId = 'favoritos' | 'padrao' | 'personalizados' | 'antigos'
const TABS: { id: TabId; label: string; icon: typeof FiStar }[] = [
  { id: 'favoritos', label: 'Favoritos', icon: FiStar },
  { id: 'padrao', label: 'Padrão', icon: FiList },
  { id: 'personalizados', label: 'Personalizados', icon: FiFileText },
  { id: 'antigos', label: 'Antigos', icon: FiClock },
]

function Hub({ onNavigate, onOpen }: { onNavigate: (v: FinView) => void; onOpen: (r: Base, config?: Cfg) => void }) {
  const [tab, setTab] = useState<TabId>('padrao')

  const cards: { key: string; title: string; desc: string; icon: typeof FiFileText; onClick?: () => void; soon?: boolean }[] = [
    { key: 'dre', title: 'DRE', desc: 'Demonstração do resultado — receitas − despesas por categoria, por competência ou caixa.', icon: FiFileText, onClick: () => onOpen('dre') },
    { key: 'fluxo', title: 'Fluxo de caixa', desc: 'Entradas e saídas realizadas com saldo acumulado, diário ou mensal.', icon: FiTrendingUp, onClick: () => onNavigate('fluxo-caixa') },
    { key: 'analise', title: 'Análise por categoria', desc: 'Distribuição por categoria com AV%, em aberto, vencido e baixado.', icon: FiPieChart, onClick: () => onNavigate('fluxo-caixa') },
    { key: 'relacao', title: 'Relação de contas a pagar/receber', desc: 'Posição por vencimento: a vencer, vencendo hoje, vencido e baixado.', icon: FiList, onClick: () => onOpen('relacao') },
  ]

  return (
    <PanelShell
      titulo="Relatórios"
      sub="DRE, fluxo de caixa, categorias e a relação de contas, prontos para abrir"
      icone={<FiFileText />}
      acoes={
        <div className="flex items-center gap-2">
          <button onClick={() => onOpen('dre')} title="Abra um relatório e clique em Salvar para criar um personalizado" className="neu-convex rounded-lg border border-border text-sm font-medium px-3 py-2 hover:bg-secondary">Novo relatório personalizado</button>
          <button disabled title="Depende de integração de entrega (e-mail/WhatsApp)" className="neu-convex rounded-lg border border-border text-sm font-medium px-3 py-2 opacity-40 cursor-not-allowed">Agendador</button>
        </div>
      }>
      {/* PLANOS (21/10/2026): abas numa PlnBarra; os botões subiram pro cabeçalho. */}
      <PlnBarra>
        <PlnBarraRot>Ver</PlnBarraRot>
        <div className="neu-seg inline-flex rounded-lg border border-border p-0.5 bg-card">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setTab(id)}
              className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-colors ${tab === id ? 'neu-relief bg-primary/10 text-primary font-medium' : 'neu-seg-btn text-muted-foreground hover:text-foreground'}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
      </PlnBarra>

      {/* PLANOS (21/10/2026, subtelas): o conteúdo de cada aba mora num PlnBloco
          com título; os cartões de relatório ganharam respiro e texto ≥ 13px. */}
      {tab === 'padrao' ? (
        <PlnBloco titulo="Relatórios padrão" nota={`${cards.length} relatórios · clique para abrir`} className="flex-1 min-h-0">
          <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              {cards.map(c => {
                const Icon = c.icon
                return (
                  <button key={c.key} onClick={c.onClick} disabled={c.soon}
                    className={`text-left rounded-xl border border-border bg-card px-4 py-4 flex gap-3.5 transition-colors ${c.soon ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary/40 hover:bg-secondary/30'}`}>
                    <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[15px] font-semibold text-foreground">{c.title}</span>
                        {c.soon && <span className="neu-tag text-xs rounded-full bg-secondary text-muted-foreground px-2 py-0.5">em breve</span>}
                      </div>
                      <p className="text-[13px] leading-snug text-muted-foreground mt-1.5">{c.desc}</p>
                    </div>
                    {!c.soon && <FiChevronRight className="w-4 h-4 text-muted-foreground self-center shrink-0" />}
                  </button>
                )
              })}
            </div>
          </div>
        </PlnBloco>
      ) : tab === 'personalizados' ? (
        <SavedReportsList onOpen={onOpen} />
      ) : (
        <PlnBloco
          titulo={tab === 'favoritos' ? 'Favoritos' : 'Antigos'}
          nota={tab === 'favoritos' ? 'Relatórios marcados com estrela' : 'Relatórios gerados anteriormente'}
          className="flex-1 min-h-0">
          <div className="flex-1 min-h-[220px] flex items-center justify-center p-10">
            <div className="text-center">
              <div className="mx-auto mb-3 w-12 h-12 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                {(() => { const Icon = TABS.find(t => t.id === tab)!.icon; return <Icon className="w-6 h-6 text-primary/80" /> })()}
              </div>
              <p className="text-sm text-muted-foreground">
                {tab === 'favoritos' && 'Você ainda não marcou relatórios como favoritos.'}
                {tab === 'antigos' && 'Nenhum relatório gerado anteriormente.'}
              </p>
            </div>
          </div>
        </PlnBloco>
      )}
    </PanelShell>
  )
}

// ── Personalizados (relatórios salvos) ──
interface SavedReport { id: number; name: string; base: Base; config: Cfg }
function SavedReportsList({ onOpen }: { onOpen: (base: Base, config?: Cfg) => void }) {
  const { authenticatedFetch } = useAuth()
  const [rows, setRows] = useState<SavedReport[]>([])
  const [loading, setLoading] = useState(true)
  const load = useCallback(async () => {
    setLoading(true)
    try {
      const d = await authenticatedFetch('/api/saved-reports').then(r => r.json())
      if (d.success) setRows(d.reports.map((r: SavedReport & { config: string | Cfg }) => {
        let config = r.config as Cfg
        if (typeof r.config === 'string') { try { config = JSON.parse(r.config) } catch { config = {} } }
        return { ...r, config }
      }))
    } finally { setLoading(false) }
  }, [authenticatedFetch])
  useEffect(() => { load() }, [load])

  const del = async (id: number) => {
    if (!confirm('Excluir este relatório personalizado?')) return
    const d = await authenticatedFetch(`/api/saved-reports/${id}`, { method: 'DELETE' }).then(r => r.json())
    if (d.success) load()
  }

  // PLANOS (21/10/2026, subtelas): a aba Personalizados é um PlnBloco com a contagem na nota.
  return (
    <PlnBloco titulo="Personalizados" nota={loading ? 'carregando…' : rows.length ? `${rows.length} salvo(s) · clique para abrir` : 'nenhum salvo'} className="flex-1 min-h-0">
      {loading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground"><FiLoader className="w-5 h-5 animate-spin" /></div>
      ) : !rows.length ? (
        <div className="flex-1 min-h-[220px] flex items-center justify-center p-10">
          <div className="text-center max-w-md">
            <div className="mx-auto mb-3 w-12 h-12 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center"><FiFileText className="w-6 h-6 text-primary/80" /></div>
            <p className="text-sm text-muted-foreground">Nenhum relatório personalizado ainda. Abra o DRE ou a Relação, ajuste os filtros e clique em <strong>Salvar</strong>.</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            {rows.map(r => (
              <div key={r.id} className="rounded-xl border border-border bg-card px-4 py-3.5 flex gap-3 items-center">
                <button onClick={() => onOpen(r.base, r.config)} className="flex gap-3 items-center flex-1 min-w-0 text-left hover:opacity-80">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                    {r.base === 'dre' ? <FiFileText className="w-5 h-5 text-primary" /> : <FiList className="w-5 h-5 text-primary" />}
                  </div>
                  <div className="min-w-0">
                    <div className="text-[15px] font-semibold text-foreground truncate">{r.name}</div>
                    <div className="text-[13px] text-muted-foreground">{r.base === 'dre' ? 'DRE' : 'Relação a pagar/receber'}</div>
                  </div>
                </button>
                <button onClick={() => del(r.id)} title="Excluir" className="w-9 h-9 flex items-center justify-center rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-500/10 shrink-0"><FiTrash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </PlnBloco>
  )
}

// ── DRE (comparativo mensal, 2026-07-31 — pedido da diretoria) ──
// Consome /api/dre/mensal: linhas canônicas com subtotais, um mês por coluna,
// movimentação patrimonial FORA do resultado. O sinal já vem do backend
// (receita +, despesa −) — aqui só se apresenta e se exporta.
interface DreLinha { porMes: number[]; total: number }
interface DreCat extends DreLinha { id: number | null; nome: string }
type SubKey = 'receitaLiquida' | 'resultadoOperacional' | 'resultadoLiquido'
interface DreSecao extends DreLinha { key: string; label: string; categorias: DreCat[]; subtotal?: { key: SubKey; label: string } | null }
interface DreMensal {
  from: string; to: string; regime: 'competencia' | 'caixa'; months: string[]
  secoes: DreSecao[]
  linhasCalculadas: Record<SubKey, DreLinha> & { margemPct: { porMes: (number | null)[]; total: number | null } }
  patrimonial: { label: string; entradas: DreSecao; saidas: DreSecao; liquido: DreLinha }
  naoClassificado: DreSecao | null
}

const fmtNum = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const round2 = (n: number) => Math.round(n * 100) / 100

function DREReport({ onBack, initial }: { onBack: () => void; initial?: Cfg }) {
  const { authenticatedFetch } = useAuth()
  // Padrão: últimos 6 meses — um DRE de comparativo com 1 coluna não compara nada.
  const [from, setFrom] = useState(initial?.from || addMonth(thisMonth(), -5))
  const [to, setTo] = useState(initial?.to || thisMonth())
  const [regime, setRegime] = useState<'competencia' | 'caixa'>(initial?.regime === 'caixa' ? 'caixa' : 'competencia')
  const [saveOpen, setSaveOpen] = useState(false)
  const [data, setData] = useState<DreMensal | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<Set<string>>(new Set())
  const [pdfBusy, setPdfBusy] = useState(false)
  const [xlsxBusy, setXlsxBusy] = useState(false)

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const params = new URLSearchParams({ from, to, regime })
      const r = await authenticatedFetch(`/api/dre/mensal?${params}`)
      const d = await r.json()
      if (d.success) setData(d); else setError(d.error || 'Falha ao carregar')
    } catch { setError('Erro de conexão') }
    finally { setLoading(false) }
  }, [authenticatedFetch, from, to, regime])
  useEffect(() => { load() }, [load])

  const nMeses = data?.months.length || 1
  const toggle = (k: string) => setOpen(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n })

  // Matriz linha-a-linha do relatório — base única do CSV e do XLSX, pra
  // exportação nunca divergir da tela.
  const montarMatriz = (d: DreMensal): (string | number | null)[][] => {
    const header = ['Linha', ...d.months.map(monthLabel), 'Total', 'Média']
    const rows: (string | number | null)[][] = [header]
    const push = (nome: string, l: DreLinha, indent = 0) =>
      rows.push([`${'    '.repeat(indent)}${nome}`, ...l.porMes, l.total, round2(l.total / d.months.length)])
    const pushSecao = (sec: DreSecao) => { push(sec.label, sec); sec.categorias.forEach(c => push(c.nome, c, 1)) }
    for (const sec of d.secoes) {
      pushSecao(sec)
      if (sec.subtotal) push(`= ${sec.subtotal.label}`, d.linhasCalculadas[sec.subtotal.key])
    }
    rows.push(['Margem líquida %', ...d.linhasCalculadas.margemPct.porMes, d.linhasCalculadas.margemPct.total, null])
    rows.push([])
    rows.push([d.patrimonial.label])
    pushSecao(d.patrimonial.entradas)
    pushSecao(d.patrimonial.saidas)
    push('= Líquido patrimonial', d.patrimonial.liquido)
    if (d.naoClassificado) { rows.push([]); pushSecao(d.naoClassificado) }
    return rows
  }

  const exportCsv = () => {
    if (!data) return
    const lines = montarMatriz(data).map(r =>
      r.map(v => typeof v === 'number' ? v.toFixed(2).replace('.', ',') : v ?? '').join(';'))
    const csv = '﻿' + lines.join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
    const a = document.createElement('a'); a.href = url; a.download = `dre_${from}_${to}.csv`; a.click(); URL.revokeObjectURL(url)
  }

  const exportXlsx = async () => {
    if (!data) return
    setXlsxBusy(true)
    try {
      const XLSX = await import('xlsx')
      const matriz = montarMatriz(data)
      const ws = XLSX.utils.aoa_to_sheet(matriz)
      ws['!cols'] = [{ wch: 44 }, ...matriz[0].slice(1).map(() => ({ wch: 14 }))]
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'DRE')
      // Aba Detalhe: formato longo (seção/categoria/mês/valor) pra tabela dinâmica.
      const detalhe: Record<string, string | number>[] = []
      const addDet = (secao: string, cats: DreCat[]) => cats.forEach(c =>
        data.months.forEach((m, i) => { if (c.porMes[i]) detalhe.push({ 'Seção': secao, 'Categoria': c.nome, 'Mês': m, 'Valor': c.porMes[i] }) }))
      data.secoes.forEach(s => addDet(s.label, s.categorias))
      addDet(data.patrimonial.entradas.label, data.patrimonial.entradas.categorias)
      addDet(data.patrimonial.saidas.label, data.patrimonial.saidas.categorias)
      if (data.naoClassificado) addDet(data.naoClassificado.label, data.naoClassificado.categorias)
      const ws2 = XLSX.utils.json_to_sheet(detalhe)
      if (detalhe.length) ws2['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { c: 0, r: 0 }, e: { c: 3, r: detalhe.length } }) }
      XLSX.utils.book_append_sheet(wb, ws2, 'Detalhe')
      XLSX.writeFile(wb, `dre_${from}_${to}.xlsx`)
    } catch (e) {
      console.error('[DRE:xlsx]', e)
      setError(e instanceof Error ? e.message : 'Falha ao exportar XLSX')
    } finally { setXlsxBusy(false) }
  }

  // PDF vem pronto do servidor (mesmos dados desta tela) — 1 clique, 1 arquivo.
  const exportPdf = async () => {
    setPdfBusy(true); setError(null)
    try {
      const params = new URLSearchParams({ from, to, regime })
      const r = await authenticatedFetch(`/api/dre/mensal/pdf?${params}`, { timeout: 120000 } as RequestInit)
      if (!r.ok) { const e = await r.json().catch(() => ({} as { error?: string })); throw new Error(e.error || `Erro ${r.status}`) }
      const url = URL.createObjectURL(await r.blob())
      const a = document.createElement('a'); a.href = url; a.download = `dre_${from}_${to}.pdf`; a.click(); URL.revokeObjectURL(url)
    } catch (e) { setError(e instanceof Error ? e.message : 'Falha ao gerar o PDF') }
    finally { setPdfBusy(false) }
  }

  const negCls = 'text-red-600 dark:text-red-400'
  const Cel = ({ v, bold = false, pct = false }: { v: number | null; bold?: boolean; pct?: boolean }) => (
    <td className={`px-3 py-1.5 text-right whitespace-nowrap tabular-nums ${bold ? 'font-semibold' : ''} ${v != null && v < 0 ? negCls : ''}`}>
      {v == null || v === 0 ? <span className="text-muted-foreground/50">—</span> : pct ? `${fmtNum(v)}%` : fmtNum(v)}
    </td>
  )
  const celulas = (l: { porMes: (number | null)[]; total: number | null }, opts: { bold?: boolean; pct?: boolean } = {}) => (
    <>
      {l.porMes.map((v, i) => <Cel key={i} v={v} {...opts} />)}
      <Cel v={l.total} {...opts} />
      <Cel v={opts.pct || l.total == null ? null : round2(l.total / nMeses)} bold={opts.bold} />
    </>
  )
  const labelTd = (extra = '') => `px-3 py-1.5 sticky left-0 bg-card z-[1] ${extra}`

  const linhaSecao = (sec: DreSecao, tone = '') => (
    <tr key={sec.key} className="border-b border-border/60">
      <td className={labelTd(`font-medium ${tone}`)}>
        {sec.categorias.length > 0 ? (
          <button onClick={() => toggle(sec.key)} className="inline-flex items-center gap-1.5 hover:text-primary">
            {open.has(sec.key) ? <FiChevronDown className="w-3.5 h-3.5 shrink-0" /> : <FiChevronRight className="w-3.5 h-3.5 shrink-0" />}
            {sec.label} <span className="text-xs text-muted-foreground font-normal">({sec.categorias.length})</span>
          </button>
        ) : <span className="pl-5">{sec.label}</span>}
      </td>
      {celulas(sec)}
    </tr>
  )
  const linhasCat = (sec: DreSecao) => open.has(sec.key) ? sec.categorias.map(c => (
    <tr key={`${sec.key}-${c.id}-${c.nome}`} className="border-b border-border/40 bg-secondary/20">
      <td className={labelTd('pl-10 text-muted-foreground')}>{c.nome}</td>
      {celulas(c)}
    </tr>
  )) : []
  const linhaSubtotal = (key: string, nome: string, l: DreLinha) => (
    <tr key={key} className="border-y border-border bg-secondary/50">
      <td className={labelTd('font-semibold bg-secondary/50')}>{`= ${nome}`}</td>
      {celulas(l, { bold: true })}
    </tr>
  )
  const linhaDivisor = (key: string, texto: string, tone = 'text-muted-foreground') => (
    <tr key={key}><td colSpan={nMeses + 3} className={`px-3 pt-5 pb-1.5 text-xs font-semibold uppercase tracking-wide ${tone}`}>{texto}</td></tr>
  )

  return (
    <PanelShell
      titulo="DRE — comparativo mensal"
      sub="Receitas menos despesas por categoria, mês a mês"
      icone={<FiFileText />}
      acoes={
        /* PLANOS (21/10/2026): o título interno virou o cabeçalho da tela; Voltar e exportações moram aqui */
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={onBack} title="Voltar ao hub" className="neu-convex inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm hover:bg-secondary">
            <FiArrowLeft className="w-4 h-4" /> Relatórios
          </button>
          <button onClick={() => setSaveOpen(true)} className="neu-convex inline-flex items-center gap-2 rounded-lg border border-border text-sm font-medium px-3 py-2 hover:bg-secondary"><FiSave className="w-4 h-4" /><span className="hidden sm:inline">Salvar</span></button>
          <button onClick={exportCsv} disabled={!data} className="neu-convex inline-flex items-center gap-2 rounded-lg border border-border text-sm font-medium px-3 py-2 hover:bg-secondary disabled:opacity-40"><FiDownload className="w-4 h-4" />CSV</button>
          <button onClick={exportXlsx} disabled={!data || xlsxBusy} className="neu-convex inline-flex items-center gap-2 rounded-lg border border-border text-sm font-medium px-3 py-2 hover:bg-secondary disabled:opacity-40">{xlsxBusy ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiDownload className="w-4 h-4" />}XLSX</button>
          <button onClick={exportPdf} disabled={!data || pdfBusy} className="neu-convex inline-flex items-center gap-2 rounded-lg border border-border text-sm font-medium px-3 py-2 hover:bg-secondary disabled:opacity-40">{pdfBusy ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiPrinter className="w-4 h-4" />}PDF</button>
          {saveOpen && <SaveReportModal base="dre" config={{ from, to, regime }} onClose={() => setSaveOpen(false)} onSaved={() => setSaveOpen(false)} />}
        </div>
      }>
      {/* filtros: período + regime */}
      <PlnBarra>
        <PlnBarraRot>Período</PlnBarraRot>
        <label className="text-sm text-muted-foreground flex items-center gap-2">De
          <input type="month" value={from} onChange={e => setFrom(e.target.value)} className="neu-convex rounded-md border border-border bg-card px-2 py-1.5 text-sm" />
        </label>
        <label className="text-sm text-muted-foreground flex items-center gap-2">até
          <input type="month" value={to} onChange={e => setTo(e.target.value)} className="neu-convex rounded-md border border-border bg-card px-2 py-1.5 text-sm" />
        </label>
        <PlnBarraSep />
        <PlnBarraRot>Regime</PlnBarraRot>
        <div className="neu-seg inline-flex rounded-lg border border-border p-0.5 bg-card">
          {([['competencia', 'Competência'], ['caixa', 'Caixa']] as ['competencia' | 'caixa', string][]).map(([v, lbl]) => (
            <button key={v} onClick={() => setRegime(v)} className={`px-3 py-1.5 text-sm rounded-md transition-colors ${regime === v ? 'neu-relief bg-primary/10 text-primary font-medium' : 'neu-seg-btn text-muted-foreground hover:text-foreground'}`}>{lbl}</button>
          ))}
        </div>
      </PlnBarra>

      {error && <div className="shrink-0 neu-relief rounded-lg border border-red-500/30 bg-red-500/5 px-3.5 py-2.5 text-sm text-red-600 dark:text-red-400">{error}</div>}
      {data?.naoClassificado && (
        <div className="shrink-0 neu-relief rounded-lg border border-amber-500/30 bg-amber-500/5 px-3.5 py-2.5 text-sm text-amber-600 dark:text-amber-400 flex items-center gap-2">
          <FiAlertTriangle className="w-4 h-4 shrink-0" />
          {data.naoClassificado.categorias.length} categoria(s) sem classificação DRE — aparecem no fim do relatório. Classifique em Cadastros → Categorias financeiras.
        </div>
      )}

      {/* Tabela — única região com scroll (vertical E horizontal) */}
      <PlnBloco titulo="Demonstrativo" nota="Valores em R$ · receitas somam, despesas subtraem" className="flex-1 min-h-[280px]">
      <div className="neu-table glass-panel flex-1 min-h-0 overflow-auto scrollbar-thin">
        {loading ? (
          <div className="h-40 flex items-center justify-center"><FiLoader className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : data && (
          <table className="w-full text-sm" style={{ minWidth: `${280 + (nMeses + 2) * 110}px` }}>
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                <th className={`px-3 py-2.5 font-medium min-w-[260px] ${stickyTh} left-0 z-20`}>
                  {monthLabel(data.from)} — {monthLabel(data.to)} · {data.regime === 'caixa' ? 'caixa (realizado)' : 'competência'}
                </th>
                {data.months.map(m => <th key={m} className={`px-3 py-2.5 font-medium text-right capitalize ${stickyTh}`}>{monthLabel(m)}</th>)}
                <th className={`px-3 py-2.5 font-medium text-right ${stickyTh}`}>Total</th>
                <th className={`px-3 py-2.5 font-medium text-right ${stickyTh}`}>Média</th>
              </tr>
            </thead>
            <tbody>
              {data.secoes.flatMap(sec => [
                linhaSecao(sec),
                ...linhasCat(sec),
                ...(sec.subtotal ? [linhaSubtotal(sec.subtotal.key, sec.subtotal.label, data.linhasCalculadas[sec.subtotal.key])] : []),
              ])}
              <tr className="border-b border-border bg-secondary/50">
                <td className={labelTd('font-semibold bg-secondary/50')}>Margem líquida %</td>
                {celulas(data.linhasCalculadas.margemPct, { bold: true, pct: true })}
              </tr>

              {(data.patrimonial.entradas.categorias.length > 0 || data.patrimonial.saidas.categorias.length > 0) && [
                linhaDivisor('div-pat', `${data.patrimonial.label} — empréstimos, aplicações, imobilizado, distribuição de lucro`),
                linhaSecao(data.patrimonial.entradas, 'text-muted-foreground'),
                ...linhasCat(data.patrimonial.entradas),
                linhaSecao(data.patrimonial.saidas, 'text-muted-foreground'),
                ...linhasCat(data.patrimonial.saidas),
                linhaSubtotal('pat-liq', 'Líquido patrimonial', data.patrimonial.liquido),
              ]}

              {data.naoClassificado && [
                linhaDivisor('div-nc', 'Não classificado — categorias sem grupo DRE', 'text-amber-600 dark:text-amber-400'),
                linhaSecao(data.naoClassificado, 'text-amber-600 dark:text-amber-400'),
                ...linhasCat(data.naoClassificado),
              ]}
            </tbody>
          </table>
        )}
      </div>
      </PlnBloco>
    </PanelShell>
  )
}

// ── Relação de contas a pagar/receber ──
interface RelacaoItem { date: string; a_vencer: number; vencendo_hoje: number; vencido: number; baixado: number; perda: number; total: number }
interface RelacaoTotals { a_vencer: number; vencendo_hoje: number; vencido: number; baixado: number; perda: number; total: number; em_aberto: number }
interface RelacaoData { month: string; items: RelacaoItem[]; totals: RelacaoTotals }

function RelacaoReport({ onBack, initial }: { onBack: () => void; initial?: Cfg }) {
  const { authenticatedFetch } = useAuth()
  const [month, setMonth] = useState(initial?.month || thisMonth())
  const [dateType, setDateType] = useState<'vencimento' | 'competencia'>(initial?.dateType === 'competencia' ? 'competencia' : 'vencimento')
  const [kind, setKind] = useState(initial?.kind || '')
  const [data, setData] = useState<RelacaoData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saveOpen, setSaveOpen] = useState(false)

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const params = new URLSearchParams({ month, dateType })
      if (kind) params.set('kind', kind)
      const r = await authenticatedFetch(`/api/relacao?${params}`)
      const d = await r.json()
      if (d.success) setData(d); else setError(d.error || 'Falha ao carregar')
    } catch { setError('Erro de conexão') }
    finally { setLoading(false) }
  }, [authenticatedFetch, month, dateType, kind])
  useEffect(() => { load() }, [load])

  const items = data?.items || []
  const money = (n: number, tone = '') => n ? <span className={tone}>{brl(n)}</span> : <span className="text-muted-foreground">—</span>

  const exportCsv = () => {
    const head = ['Vencimento', 'A vencer', 'Vencendo hoje', 'Vencido', 'Baixado', 'Perda', 'Total']
    const lines = items.map(r => [ptDate(r.date), r.a_vencer, r.vencendo_hoje, r.vencido, r.baixado, r.perda, r.total]
      .map(v => typeof v === 'number' ? v.toFixed(2).replace('.', ',') : v).join(';'))
    const csv = '﻿' + [head.join(';'), ...lines].join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
    const a = document.createElement('a'); a.href = url; a.download = `relacao-${month}.csv`; a.click(); URL.revokeObjectURL(url)
  }

  return (
    <PanelShell
      titulo="Relação a pagar/receber"
      sub="Posição dia a dia: a vencer, vencendo hoje, vencido e baixado"
      icone={<FiFileText />}
      acoes={
        /* PLANOS (21/10/2026): o título interno virou o cabeçalho da tela; Voltar e exportações moram aqui */
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={onBack} title="Voltar ao hub" className="neu-convex inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm hover:bg-secondary">
            <FiArrowLeft className="w-4 h-4" /> Relatórios
          </button>
          <button onClick={() => setSaveOpen(true)} className="neu-convex inline-flex items-center gap-2 rounded-lg border border-border text-sm font-medium px-3 py-2 hover:bg-secondary"><FiSave className="w-4 h-4" /><span className="hidden sm:inline">Salvar</span></button>
          <button onClick={exportCsv} disabled={!items.length} className="neu-convex inline-flex items-center gap-2 rounded-lg border border-border text-sm font-medium px-3 py-2 hover:bg-secondary disabled:opacity-40"><FiDownload className="w-4 h-4" /><span className="hidden sm:inline">Exportar</span></button>
          <button onClick={() => window.print()} disabled={!items.length} className="neu-convex inline-flex items-center gap-2 rounded-lg border border-border text-sm font-medium px-3 py-2 hover:bg-secondary disabled:opacity-40"><FiPrinter className="w-4 h-4" /><span className="hidden sm:inline">Imprimir</span></button>
          {saveOpen && <SaveReportModal base="relacao" config={{ month, dateType, kind }} onClose={() => setSaveOpen(false)} onSaved={() => setSaveOpen(false)} />}
        </div>
      }>
      {/* filtros */}
      <PlnBarra>
        <PlnBarraRot>Mês</PlnBarraRot>
        <div className="flex items-center gap-2">
          <button onClick={() => setMonth(m => addMonth(m, -1))} className="neu-convex w-8 h-8 flex items-center justify-center rounded-md border border-border hover:bg-secondary"><FiChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm font-medium text-foreground capitalize min-w-[140px] text-center">{monthLabelLong(month)}</span>
          <button onClick={() => setMonth(m => addMonth(m, 1))} className="neu-convex w-8 h-8 flex items-center justify-center rounded-md border border-border hover:bg-secondary"><FiChevronRight className="w-4 h-4" /></button>
        </div>
        <PlnBarraSep />
        <PlnBarraRot>Data</PlnBarraRot>
        <div className="neu-seg inline-flex rounded-lg border border-border p-0.5 bg-card">
          {([['vencimento', 'Por vencimento'], ['competencia', 'Por competência']] as ['vencimento' | 'competencia', string][]).map(([v, lbl]) => (
            <button key={v} onClick={() => setDateType(v)} className={`px-3 py-1.5 text-sm rounded-md transition-colors ${dateType === v ? 'neu-relief bg-primary/10 text-primary font-medium' : 'neu-seg-btn text-muted-foreground hover:text-foreground'}`}>{lbl}</button>
          ))}
        </div>
        <PlnBarraSep />
        <PlnBarraRot>Tipo</PlnBarraRot>
        <div className="neu-seg inline-flex rounded-lg border border-border p-0.5 bg-card">
          {([['', 'Todos'], ['receita', 'A receber'], ['despesa', 'A pagar']] as [string, string][]).map(([v, lbl]) => (
            <button key={v || 'all'} onClick={() => setKind(v)} className={`px-3 py-1.5 text-sm rounded-md transition-colors ${kind === v ? 'neu-relief bg-primary/10 text-primary font-medium' : 'neu-seg-btn text-muted-foreground hover:text-foreground'}`}>{lbl}</button>
          ))}
        </div>
      </PlnBarra>

      {error && <div className="shrink-0 neu-relief rounded-lg border border-red-500/30 bg-red-500/5 px-3.5 py-2.5 text-sm text-red-600 dark:text-red-400">{error}</div>}

      {/* Tabela — única região com scroll; cabeçalho e totais sticky.
          PLANOS: o "Em aberto" que ficava solto abaixo da tabela virou a nota do bloco. */}
      <PlnBloco
        titulo="Por dia"
        nota={data && items.length > 0
          ? <>Em aberto (a vencer + vencendo hoje + vencido): <span className="font-medium text-foreground">{brl(data.totals.em_aberto)}</span></>
          : undefined}
        className="flex-1 min-h-[280px]"
      >
      <div className="neu-table glass-panel flex-1 min-h-0 overflow-auto scrollbar-thin">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              <th className={`px-4 py-2.5 font-medium w-28 ${stickyTh}`}>{dateType === 'competencia' ? 'Competência' : 'Vencimento'}</th>
              <th className={`px-4 py-2.5 font-medium text-right ${stickyTh}`}>A vencer</th>
              <th className={`px-4 py-2.5 font-medium text-right ${stickyTh}`}>Vencendo hoje</th>
              <th className={`px-4 py-2.5 font-medium text-right ${stickyTh}`}>Vencido</th>
              <th className={`px-4 py-2.5 font-medium text-right ${stickyTh}`}>Baixado (líq.)</th>
              <th className={`px-4 py-2.5 font-medium text-right ${stickyTh}`}>Perda</th>
              <th className={`px-4 py-2.5 font-medium text-right ${stickyTh}`}>Total</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground"><FiLoader className="w-5 h-5 animate-spin inline" /></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">Nenhum lançamento no período.</td></tr>
            ) : items.map(r => (
              <tr key={r.date} className="border-b border-border/60 hover:bg-secondary/40">
                <td className="px-4 py-2 text-foreground whitespace-nowrap">{ptDate(r.date)}</td>
                <td className="px-4 py-2 text-right">{money(r.a_vencer, 'text-blue-600 dark:text-blue-400')}</td>
                <td className="px-4 py-2 text-right">{money(r.vencendo_hoje, 'text-amber-600 dark:text-amber-400')}</td>
                <td className="px-4 py-2 text-right">{money(r.vencido, 'text-red-600 dark:text-red-400')}</td>
                <td className="px-4 py-2 text-right">{money(r.baixado, 'text-primary')}</td>
                <td className="px-4 py-2 text-right">{money(r.perda, 'text-red-700 dark:text-red-300')}</td>
                <td className="px-4 py-2 text-right font-medium text-foreground">{brl(r.total)}</td>
              </tr>
            ))}
          </tbody>
          {data && items.length > 0 && (
            <tfoot>
              <tr className="font-medium">
                <td className={`px-4 py-2.5 text-muted-foreground ${stickyFoot}`}>Totais</td>
                <td className={`px-4 py-2.5 text-right text-blue-600 dark:text-blue-400 ${stickyFoot}`}>{brl(data.totals.a_vencer)}</td>
                <td className={`px-4 py-2.5 text-right text-amber-600 dark:text-amber-400 ${stickyFoot}`}>{brl(data.totals.vencendo_hoje)}</td>
                <td className={`px-4 py-2.5 text-right text-red-600 dark:text-red-400 ${stickyFoot}`}>{brl(data.totals.vencido)}</td>
                <td className={`px-4 py-2.5 text-right text-primary ${stickyFoot}`}>{brl(data.totals.baixado)}</td>
                <td className={`px-4 py-2.5 text-right text-red-700 dark:text-red-300 ${stickyFoot}`}>{brl(data.totals.perda)}</td>
                <td className={`px-4 py-2.5 text-right text-foreground ${stickyFoot}`}>{brl(data.totals.total)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      </PlnBloco>
    </PanelShell>
  )
}
